import { css } from '@emotion/react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

const container = css`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

export interface SteppedPickerItem {
  key: string;
}

export interface SteppedPickerProps<T extends SteppedPickerItem> {
  items: T[];
  selectedKey: string | null;
  onSelect: (item: T) => void;
  renderLabel: (item: T | null) => string;
  label?: string;
  ariaLabel?: string;
  prevAriaLabel?: string;
  nextAriaLabel?: string;
  minWidth?: string;
  // Order in which the chevrons cycle through `items`.
  // 'forward' (default): left = items[i-1], right = items[i+1].
  // 'backward': left = items[i+1], right = items[i-1]. Use when items are in
  // reverse-chronological order and the left chevron should walk to older entries.
  cycleDirection?: 'forward' | 'backward';
}

export function SteppedPicker<T extends SteppedPickerItem>({
  items,
  selectedKey,
  onSelect,
  renderLabel,
  label,
  ariaLabel,
  prevAriaLabel = 'previous',
  nextAriaLabel = 'next',
  minWidth = '12rem',
  cycleDirection = 'forward',
}: SteppedPickerProps<T>) {
  const [open, setOpen] = useState(false);

  const selectedIndex = useMemo(() => {
    if (selectedKey === null) {
      return -1;
    }
    return items.findIndex((it) => it.key === selectedKey);
  }, [items, selectedKey]);

  const step = cycleDirection === 'backward' ? -1 : 1;
  const prevIndex = selectedIndex - step;
  const nextIndex = selectedIndex + step;
  const canGoPrev = selectedIndex >= 0 && prevIndex >= 0 && prevIndex < items.length;
  const canGoNext = selectedIndex >= 0 && nextIndex >= 0 && nextIndex < items.length;

  const onPrev = useCallback(() => {
    if (canGoPrev) {
      onSelect(items[prevIndex]);
    }
  }, [canGoPrev, items, prevIndex, onSelect]);

  const onNext = useCallback(() => {
    if (canGoNext) {
      onSelect(items[nextIndex]);
    }
  }, [canGoNext, items, nextIndex, onSelect]);

  const onChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const item = items.find((it) => it.key === event.target.value);
      if (item) {
        onSelect(item);
      }
      setOpen(false);
    },
    [items, onSelect]
  );

  const selectedItem = selectedKey !== null ? (items.find((it) => it.key === selectedKey) ?? null) : null;

  return (
    <div css={container}>
      {label && (
        <Typography variant='body2' color='text.secondary'>
          {label}
        </Typography>
      )}
      <IconButton size='small' onClick={onPrev} disabled={!canGoPrev} aria-label={prevAriaLabel}>
        <ChevronLeftIcon color={canGoPrev ? 'secondary' : 'disabled'} />
      </IconButton>
      <Select
        size='small'
        value={selectedKey ?? ''}
        onChange={onChange}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        displayEmpty
        inputProps={ariaLabel ? { 'aria-label': ariaLabel } : undefined}
        sx={{ minWidth, fontSize: '0.875rem' }}
        renderValue={() => renderLabel(selectedItem)}
      >
        {items.map((item) => (
          <MenuItem key={item.key} value={item.key}>
            {renderLabel(item)}
          </MenuItem>
        ))}
      </Select>
      <IconButton size='small' onClick={onNext} disabled={!canGoNext} aria-label={nextAriaLabel}>
        <ChevronRightIcon color={canGoNext ? 'secondary' : 'disabled'} />
      </IconButton>
    </div>
  );
}
